/**
 * Attribution Store
 *
 * Opt-in (default OFF): inbound ad click-id capture, first-party persistence,
 * and outbound passthrough retrieval. Used by the clickIdPassthrough feature
 * in UtmLinker.js — this module owns no toggle logic of its own, only the
 * mechanics (capture / cookie / merge / URL append), all gated by the
 * `consentGranted` boolean the caller passes in.
 */

import { matchDomainPattern } from './LinkUtils.js';

/**
 * Converged set of inbound ad/attribution click-id URL params. This is the
 * cross-referenced superset used by Supabase's attribution guide, PostHog's
 * `$click_id` autocapture list, and uBlock Origin's tracking-query-param
 * filter list — do not add keys ad hoc; extend only against one of those.
 *
 * @type {string[]}
 */
export const CLICK_ID_KEYS = [
    'gclid', 'gbraid', 'wbraid', 'dclid', 'gclsrc', // Google Ads
    'gad_source',                                   // Google Ads (source signal)
    'fbclid',                                        // Meta Ads
    'msclkid',                                        // Microsoft Ads
    'yclid',                                          // Yandex Direct
    'ttclid',                                          // TikTok Ads
    'twclid',                                          // Twitter/X Ads
    'li_fat_id',                                      // LinkedIn Ads
    'epik',                                            // Pinterest Ads
    'mc_eid', 'mc_cid',                                // Mailchimp
    'igshid',                                          // Instagram
];

/** First-party cookie name used to persist captured click-ids. */
export const CLICK_ID_COOKIE_NAME = 'utm_linker_clickids';

/** Cookie lifetime, in days. */
export const CLICK_ID_COOKIE_MAX_AGE_DAYS = 90;

/**
 * Parse click-ids present in a location.search string.
 *
 * @param {string} search window.location.search (leading '?' optional)
 * @return {Object} Map of found click-id key -> value
 */
export function extractClickIdsFromSearch(search) {
    const found = {};
    if (typeof search !== 'string' || !search) return found;

    let params;
    try {
        params = new URLSearchParams(search);
    } catch (e) {
        return found;
    }

    for (const key of CLICK_ID_KEYS) {
        const value = params.get(key);
        if (value) found[key] = value;
    }

    return found;
}

/**
 * Read a single cookie's raw value out of a `document.cookie` string.
 *
 * @param {string} cookieString
 * @param {string} name
 * @return {?string}
 */
function getCookieValue(cookieString, name) {
    if (typeof cookieString !== 'string' || !cookieString) return null;

    const parts = cookieString.split(';');
    for (const part of parts) {
        const eqIdx = part.indexOf('=');
        if (eqIdx === -1) continue;

        const key = part.slice(0, eqIdx).trim();
        if (key === name) return part.slice(eqIdx + 1).trim();
    }

    return null;
}

/**
 * Read + parse the stored click-id cookie. Tolerant of a missing cookie and
 * of malformed JSON — both resolve to an empty first/last-touch map.
 *
 * @return {{first: Object, last: Object}}
 */
export function readStoredClickIds() {
    const empty = { first: {}, last: {} };

    if (typeof document === 'undefined') return empty;

    const raw = getCookieValue(document.cookie, CLICK_ID_COOKIE_NAME);
    if (!raw) return empty;

    let parsed;
    try {
        parsed = JSON.parse(decodeURIComponent(raw));
    } catch (e) {
        return empty;
    }

    if (!parsed || typeof parsed !== 'object') return empty;

    return {
        first: (parsed.first && typeof parsed.first === 'object') ? parsed.first : {},
        last: (parsed.last && typeof parsed.last === 'object') ? parsed.last : {},
    };
}

/**
 * Persist the first/last-touch click-id map to the first-party cookie.
 * `Secure` is only added when the page is served over https — so this also
 * works on http dev environments.
 *
 * @param {{first: Object, last: Object}} data
 */
export function writeStoredClickIds(data) {
    if (typeof document === 'undefined') return;

    const payload = encodeURIComponent(JSON.stringify({
        first: (data && data.first) || {},
        last: (data && data.last) || {},
    }));

    const maxAge = CLICK_ID_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const isHttps = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    const secure = isHttps ? '; Secure' : '';

    document.cookie = `${CLICK_ID_COOKIE_NAME}=${payload}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

/**
 * Capture any click-ids present in the current URL into the first-party
 * cookie: first-touch is written only for a key not already stored;
 * last-touch always reflects the newest value seen for that key. No-ops when
 * consent isn't granted, when there's no window.location to read, or when the
 * current URL carries no recognized click-id.
 *
 * @param {boolean} consentGranted Resolved consent signal (server-side + gate)
 */
export function captureClickIds(consentGranted) {
    if (!consentGranted) return;
    if (typeof window === 'undefined' || !window.location) return;

    const found = extractClickIdsFromSearch(window.location.search);
    if (Object.keys(found).length === 0) return;

    const stored = readStoredClickIds();
    const first = { ...stored.first };
    const last = { ...stored.last };

    for (const [key, value] of Object.entries(found)) {
        if (!(key in first)) {
            first[key] = value;
        }
        last[key] = value;
    }

    writeStoredClickIds({ first, last });
}

/**
 * Merged last-touch view of the stored click-ids, for the outbound
 * passthrough step. Returns an empty object when consent isn't granted —
 * gating read access here too, not just capture, per the "gate the whole
 * feature behind consent" design.
 *
 * @param {boolean} consentGranted Resolved consent signal (server-side + gate)
 * @return {Object} { [clickIdKey]: value }
 */
export function getStoredClickIds(consentGranted) {
    if (!consentGranted) return {};

    const { first, last } = readStoredClickIds();
    return { ...first, ...last };
}

/**
 * Whether a hostname matches the click-id passthrough allowlist. An empty
 * allowlist is inert by design — click-ids are never appended to any domain
 * unless it is explicitly allowlisted.
 *
 * @param {string} hostname
 * @param {string[]} allowlist
 * @return {boolean}
 */
export function matchesClickIdAllowlist(hostname, allowlist) {
    if (!hostname || !Array.isArray(allowlist) || allowlist.length === 0) return false;
    return allowlist.some((pattern) => matchDomainPattern(hostname, pattern));
}

/**
 * Append stored click-ids to an outbound URL as query params, without
 * duplicating a key already present on the URL, preserving the URL hash.
 * Mirrors appendUtmParams()'s URL-rebuild approach in LinkUtils.js.
 *
 * @param {string} url Destination URL
 * @param {Object} clickIds Click-ids to append
 * @return {string} The URL with click-ids appended, or unchanged on failure
 */
export function appendClickIdsToUrl(url, clickIds) {
    if (typeof url !== 'string' || !clickIds || Object.keys(clickIds).length === 0) {
        return url;
    }

    try {
        const urlObj = new URL(url);
        const hash = urlObj.hash;

        Object.entries(clickIds).forEach(([key, value]) => {
            if (key && value && !urlObj.searchParams.has(key)) {
                urlObj.searchParams.set(key, value);
            }
        });

        let resultUrl = urlObj.toString();
        if (hash && !resultUrl.includes(hash)) {
            resultUrl = resultUrl.replace(/#.*$/, '') + hash;
        }

        return resultUrl;
    } catch (e) {
        return url;
    }
}
